import React, { useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';

import { AuthContext } from '../../shared/context/auth-context';
import { useHttpClient } from '../../shared/hooks/http-hook';
import { useUserData, CustomFeatureClient } from '../../shared/hooks/useUserData';

// Types
interface ClientResponse {
  id: number;
  dept?: string;
  fandfUser?: string;
  domainValidation?: boolean;
  isStandardAuthActive?: boolean;
  isMicrosoftAuthActive?: boolean;
  isGoogleAuthActive?: boolean;
  sso?: any[];
  fnfDisabled?: boolean;
  logo?: string;
  logo_dark?: string;
  customFeatureClient?: string;
  eapPhoneNumber?: string;
  emergencyPhoneNumber?: string;
  pamClientCode?: string;
  open?: string;
}

interface ClientErrorResponse {
  error: {
    message: string;
    status: number;
  };
}

interface UseAccessCodeReturn {
  codeValue: string | undefined;
  errorMessage: string | undefined;
  isLoading: boolean;
  inputRef: React.RefObject<HTMLInputElement>;
  setCodeValueHandler: (event: React.ChangeEvent<HTMLInputElement>) => void;
  codeSubmitHandler: () => Promise<void>;
}

// Helper functions
const handleFandfCode = (persistUserData: any, navigate: any) => {
  persistUserData({
    clientid: '0',
    fandfUser: 'fandfch',
    domain_validation: false,
    isStandardAuthActive: true,
    isMicrosoftAuthActive: true,
    isGoogleAuthActive: true,
    singleSignOn: [],
  });
  navigate('/sign-in');
};

const mapClientResponseToUserData = (response: ClientResponse) => ({
  clientid: response.id.toString(),
  dept: response.dept,
  fandfUser: response.fandfUser,
  domain_validation: response.domainValidation,
  isStandardAuthActive: response.isStandardAuthActive,
  isMicrosoftAuthActive: response.isMicrosoftAuthActive,
  isGoogleAuthActive: response.isGoogleAuthActive,
  singleSignOn: response.sso,
  fnfDisabled: response.fnfDisabled,
  logo: response.logo,
  logo_dark: response.logo_dark,
  customFeatureClient: response.customFeatureClient as CustomFeatureClient,
  eapPhoneNumber: response.eapPhoneNumber,
  emergencyPhoneNumber: response.emergencyPhoneNumber,
  pamClientCode: response.pamClientCode,
});

export const useAccessCode = (): UseAccessCodeReturn => {
  const auth = useContext(AuthContext);
  const { persistUserData } = useUserData();
  const location = useLocation();
  const { t } = useTranslation();

  const [codeValue, setCodeValue] = useState<string | undefined>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const { isLoading, sendRequest } = useHttpClient();
  const inputRef = useRef<HTMLInputElement>(null);

  const navigate = useNavigate();
  const { codeid } = useParams();
  const [searchParams] = useSearchParams();

  const setCodeValueHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCodeValue(event.target.value);
    // Clear error when user starts typing
    if (errorMessage) {
      setErrorMessage(undefined);
    }
  };

  const codeSubmitHandler = useCallback(async () => {
    const code = codeValue || codeid;
    if (!code || code.trim() === '') {
      setErrorMessage(t('signin.empty.code'));
      return;
    }

    // Handle special fandfch code
    if (code.trim().toLowerCase() === 'fandfch') {
      handleFandfCode(persistUserData, navigate);
      return;
    }

    try {
      const response = (await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/users/getClientByCode/${encodeURIComponent(code)}/web`
      )) as ClientResponse | ClientErrorResponse;

      // Check if response has an error
      if ('error' in response && response.error) {
        setErrorMessage(response.error.message || t('R017'));
        return;
      }

      // Check if response has a valid id
      const clientResponse = response as ClientResponse;
      if (clientResponse.id !== undefined && clientResponse.id !== null) {
        persistUserData(mapClientResponseToUserData(clientResponse));

        if (clientResponse.open === 'yes') {
          setErrorMessage(undefined);

          if (codeid) {
            navigate('/signup');
          } else {
            navigate('/sign-in');
          }
        } else {
          const isPamClient = clientResponse?.customFeatureClient?.toLowerCase() === 'pam';
          setErrorMessage(isPamClient ? t('signin.pam.error') : t('R016'));
        }
      } else {
        setErrorMessage(t('R017'));
      }
    } catch (err) {
      setErrorMessage(err ? err.toString() : t('signin.error.general'));
    }
  }, [codeValue, codeid, navigate, sendRequest, persistUserData, t]);

  // Handle DirectLink from sessionStorage
  useEffect(() => {
    if (location.state?.from) {
      sessionStorage.setItem('DirectLink', location.state.from);
    }
  }, [location.state]);

  // Handle company_code from URL parameters
  useEffect(() => {
    const companyCode = searchParams.get('company_code');
    if (companyCode && !codeValue) {
      setCodeValue(companyCode);
    }
  }, [searchParams, codeValue]);

  // Handle code validation and user redirection
  useEffect(() => {
    if (codeid && !codeValue) {
      // Set the code value first, then submit will happen automatically
      setCodeValue(codeid);
    }
  }, [codeid, codeValue]);

  // Auto-submit when codeid is set
  useEffect(() => {
    if (codeid && codeValue === codeid) {
      codeSubmitHandler();
    }
  }, [codeid, codeValue, codeSubmitHandler]);

  // Handle logged in user redirection
  useEffect(() => {
    if (auth.isLoggedIn) {
      // Check if assessment complete or redirected from another page
      const storedUrl = sessionStorage.getItem('DirectLink');
      sessionStorage.removeItem('DirectLink');

      if (storedUrl && auth.tags && auth.tags.length > 0) {
        navigate(storedUrl);
      } else if (auth.tags && auth.tags.length > 0) {
        navigate('/for-you');
      } else if ((auth.tags && auth.tags.length < 1) || !auth.tags) {
        navigate('/assessment');
      }
    }
  }, [auth.isLoggedIn, auth.tags, navigate]);

  // Clear error when codeid changes (new code attempt)
  useEffect(() => {
    if (codeid) {
      setErrorMessage(undefined);
    }
  }, [codeid]);

  return {
    codeValue,
    errorMessage,
    isLoading,
    inputRef,
    setCodeValueHandler,
    codeSubmitHandler,
  };
};
